import { useEffect, type ReactElement } from 'react';
import Barcode from 'react-barcode';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';

interface DeleoPackingVerificationPageProps {
  order: OrderSummary;
  wrapPage?: (page: ReactElement) => ReactElement;
}

const ITEMS_PER_PAGE = 6;

export function DeleoPackingVerificationPage({ order, wrapPage }: DeleoPackingVerificationPageProps) {
  const totalQty = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPages = Math.max(1, Math.ceil(order.lineItems.length / ITEMS_PER_PAGE));

  // eslint-disable-next-line react-hooks/rules-of-hooks -- This top-level hook runs before label pagination.
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector(`link[href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
  }, []);

  const pages = [];
  for (let pageNum = 0; pageNum < totalPages; pageNum++) {
    const startIdx = pageNum * ITEMS_PER_PAGE;
    const pageItems = order.lineItems.slice(startIdx, startIdx + ITEMS_PER_PAGE);

    pages.push(
      <div
        key={pageNum}
        className="deleo-verification-page"
        style={{
          width: '15cm',
          height: '10cm',
          padding: '15px',
          backgroundColor: '#fff',
          pageBreakBefore: 'always',
          pageBreakAfter: pageNum < totalPages - 1 ? 'always' : 'auto',
          fontFamily: '"Noto Sans KR", sans-serif',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: '0 0 auto' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>PACKING</div>
              <div style={{ fontSize: '10px', fontWeight: 'bold', marginTop: '3px' }}>#{order.orderId}</div>
            </div>
            <div style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
              <Barcode value={order.orderId} format="CODE128" width={1.2} height={40} displayValue={false} margin={0} />
            </div>
            <div style={{ flex: '0 0 auto', fontSize: '12px', fontWeight: 'bold' }}>총수량: {totalQty}</div>
          </div>
        </div>

        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #000' }} />

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #000',
            fontSize: '9px',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#d9d9d9' }}>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  width: '8%',
                  fontWeight: 'bold',
                }}
              >
                No
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  width: '72%',
                  fontWeight: 'bold',
                }}
              >
                상품명
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  width: '20%',
                  fontWeight: 'bold',
                }}
              >
                수량
              </th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item, idx) => {
              const globalIndex = startIdx + idx + 1;
              const productName = item.nameKor || item.name || '';
              const maxChars = 50;
              const displayName =
                productName.length > maxChars ? productName.substring(0, maxChars) + '...' : productName;

              return (
                <tr key={idx}>
                  <td
                    style={{
                      border: '1px solid #000',
                      padding: '6px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {globalIndex}
                  </td>
                  <td
                    style={{
                      border: '1px solid #000',
                      padding: '6px',
                    }}
                  >
                    {displayName}
                  </td>
                  <td
                    style={{
                      border: '1px solid #000',
                      padding: '6px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: '11px',
                    }}
                  >
                    {item.quantity}
                  </td>
                </tr>
              );
            })}
            <tr style={{ backgroundColor: '#d9d9d9' }}>
              <td
                colSpan={2}
                style={{
                  border: '2px solid #000',
                  padding: '6px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '11px',
                }}
              >
                합계
              </td>
              <td
                style={{
                  border: '2px solid #000',
                  padding: '6px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '12px',
                }}
              >
                {totalQty}
              </td>
            </tr>
          </tbody>
        </table>

        <div
          style={{
            position: 'absolute',
            bottom: '15px',
            left: '15px',
            right: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '8px',
          }}
        >
          <span>[검수 후 폐기]</span>
          <span>
            {pageNum + 1}/{totalPages}
          </span>
        </div>
      </div>,
    );
  }

  return (
    <>
      <style>
        {`
          @media print {
            @page {
              size: 15cm 10cm !important;
              margin: 0 !important;
            }
            .deleo-verification-page {
              width: 15cm !important;
              height: 10cm !important;
              margin: 0 !important;
              padding: 15px !important;
              page-break-before: always !important;
            }
          }
        `}
      </style>
      {wrapPage ? pages.map(wrapPage) : pages}
    </>
  );
}
